import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const MONDAY = 1;
const TUESDAY = 2;
const WEDNESDAY = 3;
const THURSDAY = 4;
const FRIDAY = 5;
const SATURDAY = 6;
const SUNDAY = 7;
const OPENING_DEFAULT_FROM = "2020-01-21T09:00:00";
const OPENING_DEFAULT_TO = "2020-01-21T18:00:00";

export const TranslationMap = {
  [MONDAY]: "monday",
  [TUESDAY]: "tuesday",
  [WEDNESDAY]: "wednesday",
  [THURSDAY]: "thursday",
  [FRIDAY]: "friday",
  [SATURDAY]: "saturday",
  [SUNDAY]: "sunday",
} as const;

export const DEFAULT_SCHEDULE = {
  [MONDAY]: { checked: true, from: OPENING_DEFAULT_FROM, to: OPENING_DEFAULT_TO },
  [TUESDAY]: { checked: true, from: OPENING_DEFAULT_FROM, to: OPENING_DEFAULT_TO },
  [WEDNESDAY]: { checked: true, from: OPENING_DEFAULT_FROM, to: OPENING_DEFAULT_TO },
  [THURSDAY]: { checked: true, from: OPENING_DEFAULT_FROM, to: OPENING_DEFAULT_TO },
  [FRIDAY]: { checked: true, from: OPENING_DEFAULT_FROM, to: OPENING_DEFAULT_TO },
  [SATURDAY]: { checked: false, from: OPENING_DEFAULT_FROM, to: OPENING_DEFAULT_TO },
  [SUNDAY]: { checked: false, from: OPENING_DEFAULT_FROM, to: OPENING_DEFAULT_TO },
};

type WorkingDayKey = keyof typeof DEFAULT_SCHEDULE;

function formatTimeInputValue(dateTime: string) {
  return dateTime.split("T")[1]?.slice(0, 5) ?? "00:00";
}

function mergeDateAndTime(dateTime: string, time: string) {
  const date = dateTime.split("T")[0] ?? "2020-01-21";
  return `${date}T${time}:00`;
}

export default function Schedule({
  workingDays,
  onWorkingDayStatusChanged,
  onStartTimeChanged,
  onEndTimeChanged,
}: {
  workingDays: typeof DEFAULT_SCHEDULE;
  onWorkingDayStatusChanged: React.ChangeEventHandler<HTMLInputElement>;
  onStartTimeChanged(weekday: string, time: { startTime: string }): void;
  onEndTimeChanged(weekday: string, time: { endTime: string }): void;
}) {
  const intl = useIntl();
  const weekdays = Object.keys(workingDays).map(Number) as WorkingDayKey[];

  return (
    <div>
      {weekdays.map((weekday) => {
        const weekdayId = String(weekday);
        const weekdayLabel = intl.formatMessage({
          id: `clin_view.schedule.${TranslationMap[weekday]}`,
        });

        return (
          <div key={weekdayId}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <input
                id={weekdayId}
                name={weekdayId}
                checked={workingDays[weekday].checked}
                type="checkbox"
                style={{ marginTop: 0 }}
                onChange={onWorkingDayStatusChanged}
              />
              <label htmlFor={weekdayId} style={{ marginBottom: 0 }}>
                <FormattedMessage id={`clin_view.schedule.${TranslationMap[weekday]}`} />
              </label>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <input
                type="time"
                step={15 * 60}
                aria-label={`${weekdayLabel} start time`}
                value={formatTimeInputValue(workingDays[weekday].from)}
                onChange={(event) =>
                  onStartTimeChanged(weekdayId, {
                    startTime: mergeDateAndTime(
                      workingDays[weekday].from,
                      event.currentTarget.value,
                    ),
                  })
                }
              />
              <input
                type="time"
                step={15 * 60}
                aria-label={`${weekdayLabel} end time`}
                value={formatTimeInputValue(workingDays[weekday].to)}
                onChange={(event) =>
                  onEndTimeChanged(weekdayId, {
                    endTime: mergeDateAndTime(workingDays[weekday].to, event.currentTarget.value),
                  })
                }
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
